import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { PointExpression } from 'leaflet';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faCrosshairs } from '@fortawesome/free-solid-svg-icons';

/** Classe do container do controle. Usada tambem pelo ImageDownloader para
 * remover o botao do PNG exportado. */
export const RECENTER_CONTROL_CLASS = 'recenter-control';

export const RECENTER_CONTROL_LABEL = 'Recentralizar mapa';

/**
 * Monta o `onAdd` do controle: um botao real dentro do container `leaflet-bar`,
 * no mesmo visual dos controles nativos.
 *
 * O clique e registrado via `DomEvent.on`, no mesmo estilo imperativo dos outros
 * controles do projeto (`useAlertControl`, `usePolygonLegend`).
 */
export const buildRecenterOnAdd = (onRecenter: () => void) => () => {
  const container = L.DomUtil.create('div', `leaflet-bar ${RECENTER_CONTROL_CLASS}`);
  const button = L.DomUtil.create('button', 'recenter-control-button', container) as HTMLButtonElement;

  button.type = 'button';
  button.title = RECENTER_CONTROL_LABEL;
  button.setAttribute('aria-label', RECENTER_CONTROL_LABEL);
  button.innerHTML = icon(faCrosshairs).html.join('');

  // Marca o container com `_leaflet_disable_click`, que faz o mapa ignorar o
  // clique. Sem isso o clique vaza e dispara o MapClickHandler das telas de
  // cadastro — o usuario recentraliza e seleciona um local sem querer.
  L.DomEvent.disableClickPropagation(container);
  L.DomEvent.disableScrollPropagation(container);
  L.DomEvent.on(button, 'click', onRecenter);

  return container;
};

/**
 * Botao que devolve o mapa ao enquadramento de quando a tela abriu — centro e
 * zoom juntos, pelo mesmo criterio que o InteractiveMap usa na montagem.
 *
 * Fica em `bottomleft`, o unico canto livre nas cinco telas: `topleft` tem o
 * zoom do Leaflet (coberto pela caixa CAMADAS na Visao Geral Agro), `topright`
 * tem o AgroMapLayersPanel do BH Sequencial e do Indice de Cultura (e o Alert
 * do BH), e `bottomright` tem as legendas de heatmap e a attribution do tile.
 *
 * O enquadramento e capturado na primeira renderizacao (`useRef`): o alvo do
 * botao e o estado inicial, nao o que estiver valendo no momento do clique.
 */
export default function RecenterControl({
  bounds,
  center,
  zoom,
  boundsPadding,
}: {
  bounds: [number, number][];
  center?: [number, number];
  zoom?: number;
  boundsPadding: PointExpression;
}) {
  const map = useMap();
  const initialFraming = useRef({
    bounds, center, zoom, boundsPadding,
  });

  useEffect(() => {
    if (!map) return () => {};

    const framing = initialFraming.current;
    const control = new L.Control({ position: 'bottomleft' });

    control.onAdd = buildRecenterOnAdd(() => {
      if (framing.center != null && framing.zoom != null) {
        map.setView(framing.center, framing.zoom);
        return;
      }
      map.fitBounds(framing.bounds, { padding: framing.boundsPadding });
    });

    map.addControl(control);
    return () => { map.removeControl(control); };
  }, [map]);

  return null;
}
